import React from "react";
import { Calculator, ArrowUpRight } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import BannerCTA from "../CTA/CTABanner.jsx";
import Seo from "../Seo.jsx";
import guidesConfig from "../../config/guidesConfig";
import Tooltip from "./Tooltip.jsx";
import "./guides.css";

const ExtraPlaceGuide = () => {
  const meta = guidesConfig.ExtraPlaceGuide?.seo || {};

  return (
    <div className="guides-content">
            <Seo title={meta.title} description={meta.description} url={guidesConfig.ExtraPlaceGuide?.route || "/guides"}/>


      <h2>What Are Extra Place Offers?</h2>
      <p>
        Extra place offers are bookmaker promotions, typically available on horse racing and golf events. Usually, bookmakers pay out for horses or golfers finishing in the top three or four positions. With extra place offers, bookmakers extend this payout to additional positions—often fourth, fifth, or even sixth place, depending on the event.
      </p>
      <h6 className="info">
        Extra place offers create opportunities to profit if your selection finishes exactly in the extra place.
      </h6>
      <h6 className="warn">
        Extra place offers are more advanced, and can be volatile.
      </h6>

      <h2>Understanding Each-Way Bets and Place Terms</h2>
      <h6 className="info">
        An each-way bet has two parts: one bet on the selection to <em>win</em> and another bet on it to <em>place</em>.
      </h6>
      <p>
        An each-way bet – often abbreviated to <span className="key">EW</span> or <span className="key">E/W</span> – is effectively two separate bets bundled together.
      </p>
      <p>
        Selections place if they finish within specified positions, such as the top three or four. 
      </p>
      <p>
        The place part of the bet pays out at reduced odds compared to the win odds according to the <span className="key">place terms</span> – often 1/4 or 1/5 of the win odds. 
      </p>
      <div className="example-container">
        <h4>Example</h4>
      <p>
        You place a £10 each-way bet (£20 total) at odds of 10/1 (11.0) with place terms of 1/5. It has two parts:
      </p>
      <ul>
        <li><strong>£10</strong> at 10/1 (11.0) for the win, returning £110.</li>
        <li><strong>£10</strong> at 2/1 (3.0) (1/5 of 10/1) for placing, returning £30.</li>
      </ul>
      </div>


      <h2>What is Targeting the Extra Place?</h2>
      <h6 className="info">
        When we target the extra place, we hope to win with both the bookie <em>and</em> the exchange <em>if</em> our selection finishes exactly in the extra place.
      </h6>
      <p>
        We approac this by placing a back bet, and two lay bets:
      </p>
      <ul>
        <li><strong><span className="back">Back</span> bet:</strong> An each-way bet with the bookmaker.</li>
        <li><strong><span className="lay">Lay</span> the win:</strong> A bet at the exchange against your selection winning.</li>
        <li><strong><span className="lay">Lay</span> the place:</strong> A bet at the exchange against your selection finishing in standard payout positions.</li>
      </ul>
      <p>
        You aim to minimise the <Tooltip content="Small accepted loss from matched bets aiming for potential profits.">qualifying loss</Tooltip>. If your selection finishes exactly in the extra place, both bets pay out, ensuring profit.
      </p>
      <div className="example-container">
        <h4>Example</h4>
        <p>
          William Hill are paying 1 extra place at the 15:20 Cheltenham, a 10-horse race. Normally, William Hill pay would pay positions 1, 2 and 3. With the extra place offer, they pay out for positions 1, 2, 3 and 4.
          </p>
        <ul>
          <li>We place a <span className="back">back</span> bet £10 E/W on Djelo with the bookmaker at odds of 13.0. That's a £20 bet in total, £10 on Djelo to win, and £10 on Djelo to place.</li>
          <li>We <span className="lay">lay</span> Djelo to win at odds of 14.</li>
          <li>We <span className="lay">lay</span> Djelo to place at odds of 3.3.</li>
        </ul>
        <p>
          We have to accept a small qualifying loss.
        </p>
          <li>If Djelo wins, we'll lose 46p.</li>
          <li>If Djelo places, we'll lose 41p.</li>
          <li>If Djelo loses, we'll lose 40p.</li>
          <li><strong>But if Djelo comes in position 4, we'll make £33.59.</strong></li>
      </div>
      




      <CTALarge 
        to="/calculators/extra-place" 
        text="Go to the Extra Place Calculator" 
        icon={Calculator} 
      />

<h2>What Are Implied Odds?</h2>
<h6 className="info">
  Implied odds express the potential profit compared to your qualifying loss.
</h6>
<p>
  <span className="key">Implied odds</span> help us assess value by comparing how much we could lose to how much we could gain.
</p>
<p>
  For example, if placing a bet has a qualifying loss of £2.11, but the potential profit if successful is £167.89, the implied odds are calculated as:
</p>
<h6 className="info">
  Implied Odds = (Potential Profit / Qualifying Loss) + 1
</h6>
<p>
  Using this example:
</p>
<ul>
  <li>£167.89 / £2.11 = 79.57</li>
  <li>79.57 + 1 = <strong>80.57</strong></li>
</ul>

<h2>How Are Implied Odds Useful?</h2>
<p>
  Implied odds help matched bettors evaluate bets in extra-place races. They help determine if a bet offers a positive <Tooltip content="Expected Value">EV</Tooltip>.
</p>
<p>
  A formula used to quickly assess positive <Tooltip content="Expected Value">EV</Tooltip> for extra-place bets is:
</p>
<h6 className="info">
  Positive EV Odds = (Number of Runners × 1.5) / Number of Extra Places
</h6>
<p>
  For instance, in a race with 20 runners and only one extra place offered (e.g., 4th place):
</p>
<ul>
  <li>(20 runners × 1.5) / 1 extra place = <strong>30</strong></li>
</ul>
<p>
  Any implied odds above 30 indicate positive <Tooltip content="Expected Value">EV</Tooltip>, so our earlier example's implied odds of 80.57 looks great value.
</p>
<section>
  <h2>Important: Extra Places Are a Numbers Game</h2>
  <h6 className="warn">
    Expect volatility. Extra place offers require a long-term mindset.
  </h6>
  <p>Extra place offers are not a guaranteed money-maker in every race.</p>

  <p>While hitting an extra place can lead to significant profits, the hit rate is relatively low. Most of the time, your selections won’t land in that <em>exact</em> position. You’ll take small qualifying losses, race after race, and occasionally land a big win. That’s the strategy.</p>

  <p><strong>So be prepared:</strong></p>
  <ul>
    <li>Expect losing runs where you just rack up qualifying losses.</li>
    <li>Keep your stakes reasonable, especially early on.</li>
    <li>Track your <span className="key">implied odds</span> and prioritise bets with strong <Tooltip content="Expected Value">EV</Tooltip>.</li>
    <li>Play the long game — this strategy rewards consistency and patience.</li>
  </ul>
</section>
  

      <h2>Common Mistakes to Avoid</h2>
      <ul>
        <li>Make sure there's enough <Tooltip content="Amount of money available in the betting exchange for matched bets.">liquidity</Tooltip> at the exchange.</li>
        <li>Make sure you have enough money at the exchange to cover the bets.</li>
        <li>Don't skip smaller races; they often offer great opportunities.</li>
        <li>Always confirm bookmaker offer terms carefully.</li>
      </ul>
      <section>
  <h2>Other Approaches to Extra Place Offers</h2>
  <p>Beyond the standard extra place method (hoping for a horse to land exactly in the extra place), there are a few other strategies to profit from these offers. Though we won't cover them here, there are three alternatives:</p>
  <ul>
    <li><strong>Arbing the Place Market:</strong> Lock in small profits by laying in a higher place market (e.g. 4 places) where the exchange odds are shorter — often limited by liquidity.</li>
    <li><strong>Covering the Field:</strong> Back and lay every horse in the race with minimal losses to guarantee profit if <em>any</em> horse lands in the extra place — high effort, best for big events.</li>
    <li><strong>Dutching the Place Market:</strong> Back multiple horses each-way with different bookies (no lays), balancing stakes to lock in profit — requires tight odds and works best at festivals.</li>
  </ul>
</section>
      <BannerCTA 
        to="/calculators/extra-place" 
        text="Try the Extra Place Calculator" 
        icon={ArrowUpRight} 
      />
    </div>
  );
};

export default ExtraPlaceGuide;